import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Spinner,
} from '@chakra-ui/core';
import { ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { MdOutlineQuestionAnswer } from 'react-icons/md';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { QuestionnaireTemplateListResponse } from '../../questionnaireTemplate/questionnaireTemplate.types';
import { LocaleBadge } from '../../questionnaireTemplate/questionnaireTemplateTable/LocaleBadge';

const EMPTY_QUESTION_TEMPLATES = [] as QuestionnaireTemplateListResponse[];

type QuestionnaireTemplateSelectProps = {
  onTemplateSelect?: (template: QuestionnaireTemplateListResponse | undefined) => void;
  selectedTemplate?: QuestionnaireTemplateListResponse | null;
  value?: string | null;
  questionTemplates: QuestionnaireTemplateListResponse[];
  setQuestionTemplates: React.Dispatch<React.SetStateAction<QuestionnaireTemplateListResponse[]>>;
};

export const QuestionnaireTemplateSelect = React.memo(
  React.forwardRef<HTMLButtonElement, QuestionnaireTemplateSelectProps>(
    (
      {
        onTemplateSelect,
        selectedTemplate: parentSelectedTemplate,
        value,
        questionTemplates,
        setQuestionTemplates: setParentQuestionTemplates,
      },
      ref,
    ) => {
      const [searchTerm, setSearchTerm] = useState('');
      const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
      const [isFetching, setIsFetching] = useState(false);
      const { t } = useTranslation();
      const accountId = useStoreState((s) => s.app.user?.account);

      const fetchQuestionTemplates = useStoreActions((actions) => actions.account.questionTemplateList);
      const setQuestionTemplateState = useStoreActions((actions) => actions.account.setQuestionTemplates);

      useEffect(() => {
        if (accountId) {
          setIsFetching(true);
          fetchQuestionTemplates({
            accountId,
            sortField: 'positionCount',
            sortDirection: 'desc',
            searchQuery: debouncedSearchTerm || undefined,
            limit: 10000,
          })
            .then((res) => {
              const templates = res.data?.templates ?? EMPTY_QUESTION_TEMPLATES;
              setQuestionTemplateState(templates);
              setParentQuestionTemplates?.(templates);
            })
            .catch(() => {})
            .finally(() => setIsFetching(false));
        }
      }, [accountId, fetchQuestionTemplates, setParentQuestionTemplates, setQuestionTemplateState, debouncedSearchTerm]);

      const handleSearchChange = (val: string) => {
        // istanbul ignore else
        if (val !== searchTerm) {
          setIsFetching(true);
          setSearchTerm(val);
        }
      };

      const selectedTemplate = parentSelectedTemplate || questionTemplates.find((template) => template.id === value);

      return (
        <Box
          w="full"
          borderRadius="12px"
          border="1px solid var(--Outline-Border-Color, #E5E7EB)"
          bg="#FFF"
          boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.05)"
        >
          <Menu matchWidth autoSelect={false}>
            {({ isOpen }) => (
              <>
                <MenuButton
                  ref={ref}
                  w="full"
                  px={4}
                  py={2}
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  _hover={{ bg: 'gray.50' }}
                >
                  <Flex align="center" justify="space-between" w="full">
                    <Flex align="center" gap={4}>
                      <MdOutlineQuestionAnswer />
                      <Flex align="center" gap={2} ml={2}>
                        <Text color="gray.600" data-testid="selected-template-name">
                          {selectedTemplate?.name || t('createJobPosition:selectTemplateDropdownTitle')}
                        </Text>

                        {selectedTemplate?.questionLocale && <LocaleBadge locale={selectedTemplate.questionLocale} />}
                      </Flex>
                    </Flex>
                    <ChevronDownIcon
                      transition="transform 0.2s"
                      transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                      color="gray.400"
                    />
                  </Flex>
                </MenuButton>

                <MenuList>
                  <Box px={3} py={2} bg="white" position="sticky" top="0" zIndex={1}>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.650" />
                      </InputLeftElement>
                      <Input
                        type="text"
                        placeholder={t('createJobPosition:searchTemplatePlaceholder')}
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="neutral.200"
                        bg="neutral.100"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </InputGroup>
                  </Box>

                  <Box maxH="260px" overflowY="auto" position="relative">
                    {isFetching && (
                      <Flex justify="center" py={4}>
                        <Spinner size="md" color="blue.500" />
                      </Flex>
                    )}
                    {!isFetching &&
                      questionTemplates.map((template) => (
                        <MenuItem
                          key={template.id}
                          onClick={() => onTemplateSelect?.(template)}
                          bg={template.id === value ? 'gray.100' : undefined}
                          data-testid={`template-item-${template.name.replace(/\s+/g, '-')}`}
                        >
                          <Text mr={2}>{template.name}</Text>
                          <LocaleBadge locale={template?.questionLocale} />
                        </MenuItem>
                      ))}
                    {!isFetching && questionTemplates.length === 0 && (
                      <Box px={4} py={2} color="gray.500" data-testid="no-templates-found">
                        {t('createJobPosition:noTemplatesFound')}
                      </Box>
                    )}
                  </Box>
                </MenuList>
              </>
            )}
          </Menu>
        </Box>
      );
    },
  ),
);

QuestionnaireTemplateSelect.displayName = 'QuestionnaireTemplateSelect';
